import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError, api, refreshSession, uploadWithProgress } from '../../lib/api.ts';
import { invalidateAfterUpload, type ConflictPolicy } from '../../lib/queries.ts';
import type { DriveFile } from '../../lib/types.ts';

/**
 * Uploads, owned by the application rather than by the Drive page.
 *
 * They used to live in that page's state, which meant the upload *was* the
 * page: navigating away unmounted the component and the work went with it, so
 * anyone sending a folder of scans had to sit and watch a list until it
 * finished. That is a long time to be unable to use the system you are
 * uploading to.
 *
 * Holding the queue here instead means the transfer outlives whatever screen
 * started it, and the progress bar at the top of the window is the one place
 * that reports it — see UploadBar.
 *
 * Still one file at a time, deliberately. Every upload is virus-scanned
 * server-side, and twenty at once is twenty concurrent scans; sequential keeps
 * memory and scanner load predictable, and makes per-file progress mean
 * something.
 */

export type UploadStatus = 'queued' | 'uploading' | 'processing' | 'done' | 'error' | 'skipped';

export interface UploadTask {
  id: string;
  filename: string;
  folderId: string | null;
  status: UploadStatus;
  /** Bytes sent, and the total. Both 0 until the transfer starts. */
  loaded: number;
  total: number;
  message?: string | undefined;
  /**
   * The file already holding these bytes, when there is one.
   *
   * Said rather than refused: two copies of a scan in two folders is an
   * ordinary thing to want. The usual reason for seeing this is that somebody
   * forgot they had already filed it, which is worth one line and no dialog.
   */
  duplicateOf?: { id: string; name: string; trashed: boolean } | undefined;
}

export interface UploadRequest {
  file: File;
  /** Where it goes, when that folder already exists. */
  folderId: string | null;
  onConflict?: ConflictPolicy | undefined;
  /**
   * The folder it belongs in, when that folder is still to be made — a file
   * from a folder upload whose folder could not be created up front. The
   * queue makes it first, and the file goes there or nowhere: never into
   * whichever folder happened to be open.
   */
  folderPath?: { segments: readonly string[]; parentId: string | null } | undefined;
}

/**
 * What is being worked out before anything can be sent.
 *
 * A folder upload has a preparation phase — making the folders, checking each
 * name against what is already there — and until it finished, nothing had
 * been queued and the screen said nothing at all. On a folder of two hundred
 * scans that is minutes of silence, and the client reasonably concluded it
 * had not started. Announced now, from the first moment, so the panel is up
 * before the first byte moves.
 */
export interface UploadPreparing {
  /** How many files are being looked at. */
  readonly total: number;
  /** How many have been settled so far. */
  readonly done: number;
  /** The folder being built, when one is. */
  readonly label?: string | undefined;
}

/**
 * Still going, as against finished either way.
 *
 * One predicate, because two of them drifting is what put stale rows in the
 * panel: the auto-clear used it and the enqueue did not.
 */
function stillGoing(task: UploadTask): boolean {
  return task.status === 'queued' || task.status === 'uploading' || task.status === 'processing';
}

interface UploadApi {
  tasks: readonly UploadTask[];
  preparing: UploadPreparing | null;
  setPreparing(state: UploadPreparing | null): void;
  /** Add files to the queue and start working through it. */
  enqueue(requests: readonly UploadRequest[]): void;
  /** Send every failed file again, into the folder it was meant for. */
  retryFailed(): void;
  /** Forget finished entries. Anything still running is left alone. */
  clearFinished(): void;
  dismiss(id: string): void;
}

const UploadContext = createContext<UploadApi | null>(null);

export function useUploads(): UploadApi {
  const api = useContext(UploadContext);
  if (!api) throw new Error('useUploads must be used inside UploadProvider');
  return api;
}

let nextId = 0;

export function UploadProvider({ children }: { children: ReactNode }) {
  const [tasks, setTasks] = useState<UploadTask[]>([]);
  const [preparing, setPreparing] = useState<UploadPreparing | null>(null);
  const client = useQueryClient();

  // The queue and the running flag live in refs: the worker loop reads them
  // between awaits, and reading them from state would give it whatever was
  // current when the loop started.
  const queue = useRef<Array<UploadTask & { request: UploadRequest }>>([]);
  const running = useRef(false);
  /** What each failed task was asked to do, so Retry can do it again. */
  const failedRequests = useRef(new Map<string, UploadRequest>());
  /** Folders made by this queue, by path, so a folder of two hundred files is made once. */
  const madeFolders = useRef(new Map<string, string | null>());

  const update = useCallback((id: string, patch: Partial<UploadTask>) => {
    setTasks((current) => current.map((task) => (task.id === id ? { ...task, ...patch } : task)));
  }, []);

  const drain = useCallback(async () => {
    if (running.current) return;
    running.current = true;

    try {
      for (;;) {
        const next = queue.current.shift();
        if (!next) break;

        const { id, request } = next;
        update(id, { status: 'uploading', total: request.file.size, message: undefined });

        /*
         * The folder first, if it is still to be made. A file whose folder
         * cannot be made fails as that file — named, and retryable — rather
         * than being filed somewhere else. Filing it in the open folder is
         * what scattered a client's folder uploads across the Drive.
         */
        let folderId = request.folderId;
        if (request.folderPath) {
          const key = JSON.stringify(request.folderPath);
          try {
            if (!madeFolders.current.has(key)) {
              const made = await persist(
                () =>
                  api.post<{ folderId: string | null }>('/api/drive/folders/ensure-path', {
                    segments: request.folderPath!.segments,
                    parentId: request.folderPath!.parentId,
                  }),
                (attempt) => update(id, { message: `Making the folder, try ${attempt}…` }),
              );
              // Without treatConflictAsDone the answer is always the folder.
              if (typeof made !== 'symbol') madeFolders.current.set(key, made.folderId);
            }
            folderId = madeFolders.current.get(key) ?? null;
          } catch (caught) {
            failedRequests.current.set(id, request);
            update(id, {
              status: 'error',
              message: `The folder “${request.folderPath.segments.join('/')}” could not be made: ${
                caught instanceof ApiError ? caught.message : 'the server did not answer'
              }`,
            });
            continue;
          }
        }

        try {
          const uploaded = await persist(
            () => {
              const form = new FormData();
              if (folderId) form.append('folderId', folderId);
              if (request.onConflict) form.append('onConflict', request.onConflict);
              form.append('file', request.file, request.file.name);
              return uploadWithProgress<DriveFile>('/api/drive/files', form, {
                onProgress: (loaded, total) => update(id, { loaded, total }),
                // The bytes are gone; what remains is the scan and, for a
                // workbook, the conversion. Neither reports progress, so the bar
                // stops pretending to know and says what it is waiting for.
                onSent: () => update(id, { status: 'processing' }),
              });
            },
            (attempt) =>
              update(id, { status: 'uploading', loaded: 0, message: `Retrying, try ${attempt}…` }),
            // A name clash on a second attempt is the first attempt having
            // landed after all, its answer lost on the way back.
            { treatConflictAsDone: true },
          );

          failedRequests.current.delete(id);
          update(id, {
            status: 'done',
            message: uploaded === ALREADY_THERE ? 'Already uploaded' : undefined,
            ...(uploaded && uploaded !== ALREADY_THERE && uploaded.duplicateOf
              ? { duplicateOf: uploaded.duplicateOf }
              : {}),
          });
          await invalidateAfterUpload(client, folderId);
        } catch (caught) {
          failedRequests.current.set(id, { ...request, folderId, folderPath: undefined });
          update(id, {
            status: 'error',
            message: caught instanceof ApiError ? caught.message : 'Upload failed.',
          });
        }
      }
    } finally {
      running.current = false;
    }
  }, [client, update]);

  const enqueue = useCallback(
    (requests: readonly UploadRequest[]) => {
      if (requests.length === 0) return;

      const added = requests.map((request) => {
        nextId += 1;
        const task: UploadTask & { request: UploadRequest } = {
          id: `upload-${nextId}`,
          filename: request.file.name,
          folderId: request.folderId,
          status: 'queued',
          loaded: 0,
          total: request.file.size,
          request,
        };
        return task;
      });

      queue.current.push(...added);

      /*
       * A new batch replaces the last one's leftovers.
       *
       * The panel clears itself a few seconds after everything lands, but not
       * when something failed — a rejected file is the one thing here worth
       * reading, and a bar that wipes it before anyone looks up is no use.
       * The cost was that a single failure pinned the panel open for the rest
       * of the session, and every upload after it was appended underneath. So
       * uploading two files reported three, one of them a rejection from ten
       * minutes ago, and the counts described a batch nobody had sent.
       *
       * Starting a new batch is the signal that the old one has been read.
       * Anything still in flight stays: a batch queued while another is
       * running is also "now", and dropping it would lose a live transfer
       * from the display.
       */
      setTasks((current) => [
        ...current.filter(stillGoing),
        ...added.map(({ request: _r, ...task }) => task),
      ]);
      void drain();
    },
    [drain],
  );

  /**
   * Drop everything that has stopped, successful or not.
   *
   * Failures included: this backs the Dismiss button, and a bar reporting
   * nothing but rejected files had a Dismiss that did nothing at all —
   * it filtered for `done`, of which there were none.
   *
   * Anything still queued or in flight is left alone, so dismissing a
   * finished batch cannot silently abandon one that is still going.
   */
  const clearFinished = useCallback(() => {
    setTasks((current) => current.filter(stillGoing));
    failedRequests.current.clear();
  }, []);

  const retryFailed = useCallback(() => {
    const again = [...failedRequests.current.values()];
    failedRequests.current.clear();
    setTasks((current) => current.filter((task) => task.status !== 'error'));
    enqueue(again);
  }, [enqueue]);

  const dismiss = useCallback((id: string) => {
    failedRequests.current.delete(id);
    setTasks((current) => current.filter((task) => task.id !== id));
  }, []);

  const value = useMemo<UploadApi>(
    () => ({ tasks, preparing, setPreparing, enqueue, retryFailed, clearFinished, dismiss }),
    [tasks, preparing, enqueue, retryFailed, clearFinished, dismiss],
  );

  return <UploadContext.Provider value={value}>{children}</UploadContext.Provider>;
}

/** Said by `persist` when a retried upload turns out to have landed the first time. */
export const ALREADY_THERE = Symbol('already there');

/**
 * Whether trying again could help: the server was unreachable, restarting,
 * busy, or took too long. A refusal — a bad name, no permission, too large —
 * will be refused again, and is reported at once instead.
 */
export function isTransient(caught: unknown): boolean {
  if (!(caught instanceof ApiError)) return true;
  return [0, 408, 425, 429, 500, 502, 503, 504].includes(caught.status);
}

/** Waits between attempts: quick at first, then long enough for a restart. */
const BACKOFF_MS = [1_000, 3_000, 8_000, 20_000, 45_000];

/**
 * Do it, and keep doing it through the failures that pass.
 *
 * Five more tries over a minute and a quarter — long enough for the API to
 * restart, or for the server's queue of large files to drain (it answers
 * "busy, try again" rather than holding everything). An expired session is
 * renewed and the attempt made again. Anything else fails at once, with the
 * server's own words.
 */
export async function persist<T>(
  attempt: () => Promise<T>,
  onRetry: (attempt: number) => void,
  options: { treatConflictAsDone?: boolean } = {},
): Promise<T | typeof ALREADY_THERE> {
  let refreshed = false;
  for (let tries = 0; ; tries += 1) {
    try {
      return await attempt();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401 && !refreshed) {
        refreshed = true;
        if (await refreshSession()) {
          tries -= 1;
          continue;
        }
      }
      if (
        options.treatConflictAsDone &&
        tries > 0 &&
        caught instanceof ApiError &&
        caught.status === 409
      ) {
        return ALREADY_THERE;
      }
      const wait = BACKOFF_MS[tries];
      if (!isTransient(caught) || wait === undefined) throw caught;
      onRetry(tries + 2);
      await new Promise((resolve) => setTimeout(resolve, wait));
    }
  }
}
